/**
 * chat-tabs Stimulus controller — the Chip AI / Support tabs of the Chip widget.
 *
 * Bound to the .pchat element of templates/components/ai_chat_widget.html
 * beside ai-chat. Owns which tab shows: the panes, the tabs' aria-selected
 * (the tabs are the panel header), and the header controls that belong to
 * Chip AI alone (chipOnly targets). Opening and closing the panel stays with
 * ai-chat.
 *
 * The tab is remembered per device in localStorage, a convenience only: every
 * access is wrapped, and a blocked store just means opening on the default.
 * The load-time choice (deep link, Chip availability, remembered tab) is the
 * pure initialTab() in _chat_tabs.js.
 *
 * A staff deep link's ?conversation=<id> is held for support-inbox
 * (takePendingConversation) before the params are stripped from the URL.
 *
 * window.openSupport is exposed for global-actions.js `data-fn` (the account
 * and mobile menu Feedback items), the same way ai-chat exposes approveWrite;
 * window.openChip likewise for those menus' Ask Chip items. An "Ask Chip" link
 * in a controller dispatches chippie:open-chip on document instead, which the
 * widget routes to openChip().
 * The widget is data-turbo-permanent, so connect() initialises once per load.
 */
import { Controller } from "@hotwired/stimulus";
import {
    initialTab, opensSupport, supportConversation, urlWithoutSupportParam,
} from "./_chat_tabs.js";
import { playOnce } from "./_sheet_motion.js";

const STORAGE_KEY = 'chippie.chatTab';
const PANE_FADE_MS = 200;   // longer than the pane fade in ai_chat.css

function readStoredTab() {
    try { return window.localStorage.getItem(STORAGE_KEY); } catch (_e) { return null; }
}

function storeTab(tab) {
    try { window.localStorage.setItem(STORAGE_KEY, tab); } catch (_e) { /* per-device nicety only */ }
}

export default class extends Controller {
    static targets = ['tab', 'pane', 'chipOnly'];
    static values = { chipAvailable: { type: Boolean, default: true } };

    connect() {
        if (this._ready) return;   // data-turbo-permanent: initialise once
        this._ready = true;
        window.openSupport = (prefill) => this.openSupport(prefill);
        window.openChip = () => this.openChip();
        if (!this.chipAvailableValue) this._tabNamed('chip').hidden = true;

        const params = new URLSearchParams(window.location.search);
        this._show(initialTab(readStoredTab(), this.chipAvailableValue, params));
        this._pendingConversation = supportConversation(params);
        if (opensSupport(params)) {
            window.history.replaceState(window.history.state, '',
                urlWithoutSupportParam(window.location.href));
            this.openSupport();
        }
    }

    // data-action="click->chat-tabs#select" on each tab button.
    // Only a tab the user picks fades in; the pane shown with the panel rides
    // the panel's own open animation.
    select(event) {
        const tab = event.currentTarget.dataset.tab;
        this._show(tab);
        storeTab(tab);
        const pane = this.paneTargets.find((el) => el.dataset.tab === tab);
        if (pane) playOnce(pane, 'pchat-pane--enter', PANE_FADE_MS);
    }

    /** The thread a staff deep link asked for, handed over once (support-inbox). */
    takePendingConversation() {
        const id = this._pendingConversation;
        this._pendingConversation = null;
        return id;
    }

    /**
     * `prefill` (Chip's "Ask the founders" hand-off) is typed into an empty
     * support composer for the user to edit and send; nothing is sent for them.
     */
    openSupport(prefill) {
        this._show('support');
        this._open();
        const pane = this.paneTargets.find((el) => el.dataset.tab === 'support');
        const input = pane && pane.querySelector('.support-input');
        if (input && typeof prefill === 'string' && prefill && !input.value) {
            input.value = prefill;
            input.dispatchEvent(new Event('input', { bubbles: true }));
            input.focus();
        }
    }

    // chippie:open-chip on document: an "Ask Chip" link elsewhere in the app
    // (the getting-started checklist) opens the panel on Chip AI.
    openChip() {
        if (!this.chipAvailableValue) return;
        this._show('chip');
        this._open();
    }

    _open() {
        const chat = this.application.getControllerForElementAndIdentifier(this.element, 'ai-chat');
        if (chat) chat.open();
    }

    _show(tab) {
        this.tabTargets.forEach((button) => {
            const selected = button.dataset.tab === tab;
            button.setAttribute('aria-selected', String(selected));
            button.tabIndex = selected ? 0 : -1;
        });
        this.paneTargets.forEach((pane) => { pane.hidden = pane.dataset.tab !== tab; });
        this.chipOnlyTargets.forEach((el) => { el.hidden = tab !== 'chip'; });
    }

    _tabNamed(tab) {
        return this.tabTargets.find((button) => button.dataset.tab === tab);
    }
}
